import type { ReactElement } from 'react';

import { useTranslation } from '@govard/i18n-core/react';
import { CheckCircle2, ExternalLink, Zap } from 'lucide-react';

import { StatusBadge } from './StatusBadge.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { DashboardItemStatus, FocusPriority } from './types.js';

export interface FocusItem {
  id: string;
  title: string;
  subtitle?: string;
  status: DashboardItemStatus;
  priority?: FocusPriority;
}

export interface FocusTodayWidgetProps {
  items?: readonly FocusItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onViewAll?: () => void;
  onItemClick?: (item: FocusItem) => void;
}

export function FocusTodayWidget({
  items = [],
  className,
  testId,
  loading = false,
  onViewAll,
  onItemClick,
}: FocusTodayWidgetProps): ReactElement {
  const { t } = useTranslation();
  const canViewAll = typeof onViewAll === 'function';
  const widgetContainerProps = {
    title: t('crm.dashboard.focus.title'),
    icon: Zap,
    iconColor: 'var(--color-warning, #d97706)',
    minHeight: 280,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <button
        type="button"
        {...(canViewAll ? { onClick: onViewAll } : {})}
        disabled={!canViewAll}
        className="font-medium flex items-center"
        style={{ fontSize: 11, color: canViewAll ? 'var(--color-primary, #c47322)' : 'var(--color-text-muted, #8a7f70)', gap: 4, cursor: canViewAll ? 'pointer' : 'default', opacity: canViewAll ? 1 : 0.6 }}
      >
        {t('crm.dashboard.focus.view_all')} <ExternalLink size={10} />
      </button>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="rounded-lg"
              style={{ height: 52, background: 'var(--color-border, #ddd7cc)', opacity: 0.4 }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full" style={{ padding: 24, opacity: 0.6 }}>
          <CheckCircle2 size={32} style={{ color: 'var(--color-success, #16a34a)', marginBottom: 12 }} />
          <p style={{ fontSize: 13, color: 'var(--color-text-muted, #8a7f70)', textAlign: 'center' }}>
            {t('crm.dashboard.focus.empty')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              {...(onItemClick ? { onClick: () => onItemClick(item) } : {})}
              disabled={!onItemClick}
              className="flex items-center justify-between rounded-lg border border-transparent transition-colors w-full text-left"
              style={{ padding: '10px 12px', gap: 12, cursor: onItemClick ? 'pointer' : 'default' }}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" style={{ fontSize: 13, color: 'var(--color-text, #2d2418)' }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted, #8a7f70)', marginTop: 2 }}>
                  {item.subtitle ?? t('crm.dashboard.focus.no_project')}
                </div>
              </div>
              <div className="flex items-center shrink-0" style={{ gap: 6 }}>
                <StatusBadge status={item.status} />
                {item.priority ? (
                  <span
                    className="font-bold"
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 4,
                      backgroundColor:
                        item.priority === 'P0' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(251, 191, 36, 0.12)',
                      color: item.priority === 'P0' ? 'var(--color-error, #dc2626)' : 'var(--color-warning, #d97706)',
                    }}
                  >
                    {item.priority}
                  </span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      )}
    </WidgetContainer>
  );
}
